import React, { useEffect, useState } from 'react';
import { FlipbaseConfig } from '../types/flipbase';

declare global {
  interface Window {
    Flipbase: any;
  }
}

interface FlipbaseRecorderProps {
  config: FlipbaseConfig;
}

export const FlipbaseRecorder: React.FC<FlipbaseRecorderProps> = ({ config }) => {
  const [videoId, setVideoId] = useState<string | null>(localStorage.getItem('flipbase_video_id'));
  const [recorderVersion, setRecorderVersion] = useState<'v1' | 'v2'>('v2');
  const recorderRef = React.useRef<any>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const isInitializingRef = React.useRef(false);
  
  const cleanupEverything = React.useCallback(() => {
    console.log('Cleaning up recorder...');
    
    // Destroy recorder instance
    if (recorderRef.current && typeof recorderRef.current.destroy === 'function') {
      try {
        recorderRef.current.destroy();
      } catch (error) {
        console.error('Error destroying recorder:', error);
      }
      recorderRef.current = null;
    }
    
    // Only remove recorder-specific DOM elements, not all flipbase elements
    // This allows the player to coexist
    const recorderElements = document.querySelectorAll('[class*="recorder"], [id*="recorder"]');
    recorderElements.forEach(el => {
      // Don't remove our own container or player containers
      if (containerRef.current && !containerRef.current.contains(el) && 
          !el.id.includes('player')) {
        try {
          el.remove();
        } catch (e) {
          console.error('Error removing element:', e);
        }
      }
    });
    
    // Remove only the current recorder script version
    const scripts = document.querySelectorAll(`script[data-recorder-version="${recorderVersion}"]`);
    scripts.forEach(script => {
      try {
        script.remove();
      } catch (e) {
        console.error('Error removing script:', e);
      }
    });
    
    // Remove only recorder-specific CSS, not all flipbase CSS
    const cssLinks = document.querySelectorAll('link[href*="recorder"], style[data-recorder]');
    cssLinks.forEach(link => {
      try {
        link.remove();
      } catch (e) {
        console.error('Error removing CSS:', e);
      }
    });
    
    // Don't delete the entire Flipbase global - it might be used by the player
    // The Flipbase.recorder object should clean itself up
    
    console.log('Recorder cleanup complete');
  }, [recorderVersion]);
  
  useEffect(() => {
    // Skip initialization if no recorderId is provided
    if (!config.recorderId) {
      console.error('recorder_id is required');
      return;
    }

    // Prevent multiple simultaneous initializations
    if (isInitializingRef.current) {
      console.log('Already initializing, skipping...');
      return;
    }

    let isDestroyed = false;
    isInitializingRef.current = true;
    
    const initializeRecorder = async () => {
      console.log(`Initializing recorder ${recorderVersion}...`);
      
      // Don't initialize if we're already destroyed
      if (isDestroyed) {
        isInitializingRef.current = false;
        return;
      }
      
      // Complete cleanup
      cleanupEverything();
      
      // Wait for cleanup to settle
      await new Promise(resolve => setTimeout(resolve, 300));
      
      if (isDestroyed) {
        isInitializingRef.current = false;
        return;
      }
      
      // Determine which URL to use
      const recorderUrl = recorderVersion === 'v1' 
        ? (process.env.REACT_APP_RECORDER_URL_V1 || 'https://s3.eu-central-1.amazonaws.com/cdn.staging.flipbase.com/recorder/recorder.js')
        : (process.env.REACT_APP_RECORDER_URL || 'https://s3.eu-central-1.amazonaws.com/cdn.staging.flipbase.com/recorder/v2/recorder.js');
      
      console.log('Loading script:', recorderUrl);
      
      // Load the correct script
      const script = document.createElement('script');
      script.src = recorderUrl;
      script.async = true;
      script.setAttribute('data-recorder-version', recorderVersion);
      
      script.onload = () => {
        console.log('Script loaded, window.Flipbase:', !!window.Flipbase);
        
        if (!isDestroyed && window.Flipbase) {
          try {
            recorderRef.current = window.Flipbase.recorder({
              recorderId: config.recorderId,
              callback: function (id: string) {
                if (!isDestroyed) {  
                  console.log('Video recorded! ID:', id);
                  localStorage.setItem('flipbase_video_id', id);
                  setVideoId(id);
                }
              }
            });
            console.log('Recorder initialized successfully');
          } catch (error) {
            console.error('Error initializing recorder:', error);
          }
        }
        isInitializingRef.current = false;
      };

      script.onerror = () => {
        console.error(`Failed to load recorder script: ${recorderUrl}`);
        isInitializingRef.current = false;
      };

      document.head.appendChild(script);
    };

    initializeRecorder();

    return () => {
      console.log('Component cleanup triggered');
      isDestroyed = true;
      isInitializingRef.current = false;
      cleanupEverything();
    };
  }, [config.recorderId, recorderVersion, cleanupEverything]);

  return (
    <div data-testid="flipbase-recorder" ref={containerRef}>
      <div style={{ marginBottom: '15px' }}>
        <button
          type="button"
          onClick={() => setRecorderVersion('v1')}
          disabled={isInitializingRef.current}
          style={{
            padding: '8px 16px',
            marginRight: '10px',
            backgroundColor: recorderVersion === 'v1' ? '#007bff' : '#e0e0e0',
            color: recorderVersion === 'v1' ? 'white' : 'black',
            border: 'none',
            borderRadius: '4px',
            cursor: isInitializingRef.current ? 'not-allowed' : 'pointer',
            fontWeight: recorderVersion === 'v1' ? 'bold' : 'normal',
            opacity: isInitializingRef.current ? 0.6 : 1
          }}
        >
          Recorder V1
        </button>
        <button
          type="button"
          onClick={() => setRecorderVersion('v2')}
          disabled={isInitializingRef.current}
          style={{
            padding: '8px 16px',
            backgroundColor: recorderVersion === 'v2' ? '#007bff' : '#e0e0e0',
            color: recorderVersion === 'v2' ? 'white' : 'black',
            border: 'none',
            borderRadius: '4px',
            cursor: isInitializingRef.current ? 'not-allowed' : 'pointer',
            fontWeight: recorderVersion === 'v2' ? 'bold' : 'normal',
            opacity: isInitializingRef.current ? 0.6 : 1
          }}
        >
          Recorder V2
        </button>
      </div>
      <form key={recorderVersion}>
        <input type="flipbase" data-testid="flipbase-recorder-widget" />
      </form>
      <div style={{ marginTop: '10px' }}>
        {videoId ? (
          <div 
            style={{ 
              padding: '10px',
              backgroundColor: '#f0f0f0',
              borderRadius: '4px',
              marginBottom: '10px'
            }}
            data-testid="flipbase-video-id-display"
          >
            <strong>Last recorded video ID:</strong> <span data-testid="flipbase-video-id-value">{videoId}</span>
          </div>
        ) : (
          <small>Record a video to get the video ID</small>
        )}
      </div>
    </div>
  );
};
